import { Close, Save } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Fade,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete,
  TextField
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { FC, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { array, boolean, object, string, number } from 'yup';
// Models
import { IAddressBookLookup, IAddressBookUser, IDropdownResponse } from '../../../../models';
// Fetch
import { addAddressBookUser, getAddressBookLookup } from '../../../../fetch';
import {
  getAddressBookRelationshipTypes,
  addressBookRelationshipTypesQueryKey
} from '../../../../fetch/email-templates';
// Components
import { Modal } from '../../../../components';
import { SalesNeighborhoodAutoComplete } from '../../../../components/dropdowns/sales-neighborhood-autocomplete';
import { SalesNotificationAssignmentAutoComplete } from '../../../../components/dropdowns/sales-notification-assignments';
import { SalesTerritoryAutoComplete } from '../../../../components/dropdowns/territory-autocomplete';

interface IAddressBookModal {
  isOpen?: boolean;
  onClose?: () => void;
  refreshData: () => void;
}

const validationSchema = object()
  .shape({
    selectedUser: string().required('Contact name is required.'),
    isGlobalContact: boolean(),
    selectedNeighborhoods: array(),
    selectedTerritories: array(),
    addressBookJobTitleId: number().nullable(),
    addressBookJobTitleName: string().nullable()
  })
  .test('neighborhoods-or-territories', null, function (values) {
    const { isGlobalContact, selectedNeighborhoods, selectedTerritories } = values;
    if (
      !isGlobalContact &&
      selectedNeighborhoods.length === 0 &&
      selectedTerritories.length === 0
    ) {
      return this.createError({
        path: 'selectedNeighborhoods',
        message: 'At least one neighborhood or territory is required.'
      });
    }
    return true;
  });

export const AddressBookModal: FC<IAddressBookModal> = ({ isOpen, onClose, refreshData }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const {
    data: addressBookUsers,
    isLoading: isLoadingUsers,
    refetch
  } = useQuery('addressBookLookup', getAddressBookLookup, {
    enabled: false // Disable automatic query execution
  });

  const { data: relationshipTypes = [], isLoading: isLoadingRelationshipTypes } = useQuery<
    IDropdownResponse[],
    Error
  >([addressBookRelationshipTypesQueryKey], () => getAddressBookRelationshipTypes());

  useEffect(() => {
    if (isOpen) {
      refetch(); // Fetch the latest contacts when the modal opens
    }
  }, [isOpen, refetch]);

  return (
    <Modal open={isOpen} onClose={onClose} maxWidth="md" title="Add Contact">
      <Fade in={isOpen}>
        <Box mt={1}>
          <Formik
            initialValues={{
              selectedUser: '',
              selectedNeighborhoods: [],
              selectedTerritories: [],
              notificationTypes: [],
              isGlobalContact: false,
              addressBookJobTitleId: null,
              addressBookJobTitleName: ''
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { resetForm, setSubmitting }) => {
              // Determine if this is a selected option or a custom typed value
              const isCustomValue = !values.addressBookJobTitleId && values.addressBookJobTitleName;
              const selectedRelationshipType = relationshipTypes.find(
                type => type.value === values.addressBookJobTitleId?.toString()
              );

              const newUser: IAddressBookUser = {
                accountName: values.selectedUser,
                neighborhoodIds: values.selectedNeighborhoods,
                territoryIds: values.selectedTerritories,
                notificationTypes: values.notificationTypes,
                isGlobal: values.isGlobalContact,
                addressBookJobTitleId: values.addressBookJobTitleId || null,
                addressBookJobTitleName: isCustomValue
                  ? values.addressBookJobTitleName
                  : selectedRelationshipType?.description || ''
              };

              try {
                await addAddressBookUser(newUser);

                // If a custom value was entered, invalidate the relationship types cache
                if (isCustomValue) {
                  queryClient.invalidateQueries([addressBookRelationshipTypesQueryKey]);
                }

                refreshData();
                onClose?.();
                resetForm();
                enqueueSnackbar('User added successfully', { variant: 'success' });
              } catch (error) {
                console.error('Error adding user:', error);
                enqueueSnackbar('Error adding user', { variant: 'error' });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, handleChange, handleSubmit, setFieldValue, errors, touched }) => (
              <Form onSubmit={handleSubmit}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      size="small"
                      error={Boolean(errors.selectedUser && touched.selectedUser)}
                    >
                      <InputLabel id="user-label">
                        {isLoadingUsers ? 'Loading...' : 'Contact'}
                      </InputLabel>
                      <Select
                        labelId="user-label"
                        id="user"
                        name="selectedUser"
                        value={values.selectedUser}
                        onChange={event => {
                          // Handle the contact selection
                          handleChange(event);

                          // Find the selected user and prefill job title
                          const selectedAccountName = event.target.value;
                          const selectedUser = addressBookUsers?.find(
                            user => user.accountName === selectedAccountName
                          );

                          if (selectedUser?.jobTitleName) {
                            // Try to find a matching relationship type
                            const matchingRelationshipType = relationshipTypes.find(
                              type =>
                                type.description.toLowerCase() ===
                                selectedUser.jobTitleName.toLowerCase()
                            );

                            if (matchingRelationshipType) {
                              // Found a match - select from dropdown
                              setFieldValue(
                                'addressBookJobTitleId',
                                parseInt(matchingRelationshipType.value)
                              );
                              setFieldValue(
                                'addressBookJobTitleName',
                                matchingRelationshipType.description
                              );
                            } else {
                              // No match - treat as custom entry
                              setFieldValue('addressBookJobTitleId', null);
                              setFieldValue('addressBookJobTitleName', selectedUser.jobTitleName);
                            }
                          } else {
                            // No job title - clear the field
                            setFieldValue('addressBookJobTitleId', null);
                            setFieldValue('addressBookJobTitleName', '');
                          }
                        }}
                        label="Contact"
                        MenuProps={{
                          sx: {
                            maxHeight: '30rem'
                          }
                        }}
                      >
                        {addressBookUsers?.map((user: IAddressBookLookup) => (
                          <MenuItem key={user.accountName} value={user.accountName}>
                            {user.displayName}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.selectedUser && touched.selectedUser && (
                        <FormHelperText error>{errors.selectedUser}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      freeSolo
                      options={relationshipTypes}
                      getOptionLabel={option =>
                        typeof option === 'string' ? option : option.description
                      }
                      value={
                        values.addressBookJobTitleId
                          ? relationshipTypes.find(
                              type => type.value === values.addressBookJobTitleId?.toString()
                            ) || null
                          : values.addressBookJobTitleName || ''
                      }
                      onChange={(_, newValue: IDropdownResponse | string | null) => {
                        if (typeof newValue === 'string') {
                          // Custom typed value
                          setFieldValue('addressBookJobTitleId', null);
                          setFieldValue('addressBookJobTitleName', newValue);
                        } else if (newValue && typeof newValue === 'object') {
                          // Selected from dropdown
                          setFieldValue('addressBookJobTitleId', parseInt(newValue.value));
                          setFieldValue('addressBookJobTitleName', newValue.description);
                        } else {
                          // Cleared
                          setFieldValue('addressBookJobTitleId', null);
                          setFieldValue('addressBookJobTitleName', '');
                        }
                      }}
                      onInputChange={(_, newInputValue) => {
                        // Update the display value when typing
                        if (!relationshipTypes.find(type => type.description === newInputValue)) {
                          setFieldValue('addressBookJobTitleName', newInputValue);
                          setFieldValue('addressBookJobTitleId', null);
                        }
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Job Title"
                          size="small"
                          fullWidth
                          error={
                            touched.addressBookJobTitleName && !!errors.addressBookJobTitleName
                          }
                          helperText={
                            touched.addressBookJobTitleName && errors.addressBookJobTitleName
                          }
                        />
                      )}
                      disabled={isLoadingUsers || isLoadingRelationshipTypes}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SalesNotificationAssignmentAutoComplete
                      limitTags={5}
                      multiple={true}
                      value={values.notificationTypes}
                      handleChange={(val: any[]) => setFieldValue('notificationTypes', val)}
                      handleClear={() => setFieldValue('notificationTypes', [])}
                    />
                    {errors.notificationTypes && touched.notificationTypes && (
                      <FormHelperText error>{errors.notificationTypes}</FormHelperText>
                    )}
                  </Grid>
                  <Grid item xs={12} style={{ padding: '0.25rem 1rem 0 1rem' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.isGlobalContact}
                          onChange={event => {
                            const isChecked = event.target.checked;
                            setFieldValue('isGlobalContact', isChecked);
                            if (isChecked) {
                              setFieldValue('selectedNeighborhoods', []);
                              setFieldValue('selectedTerritories', []);
                            }
                          }}
                          name="isGlobalContact"
                          color="primary"
                        />
                      }
                      label="Include in All Address Books"
                    />
                  </Grid>

                  {!values.isGlobalContact && (
                    <>
                      <Grid item xs={12} style={{ paddingTop: '0.5rem' }}>
                        <SalesTerritoryAutoComplete
                          limitTags={4}
                          multiple={true}
                          value={values.selectedTerritories}
                          handleChange={(val: any[]) => setFieldValue('selectedTerritories', val)}
                          handleClear={() => setFieldValue('selectedTerritories', [])}
                          disabled={values.isGlobalContact}
                        />
                        {errors.selectedTerritories && touched.selectedTerritories && (
                          <FormHelperText error>{errors.selectedTerritories}</FormHelperText>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <SalesNeighborhoodAutoComplete
                          limitTags={4}
                          value={values.selectedNeighborhoods}
                          handleChange={(val: string[] | number[]) =>
                            setFieldValue('selectedNeighborhoods', val)
                          }
                          handleClear={() => setFieldValue('selectedNeighborhoods', [])}
                          isDisabled={values.isGlobalContact}
                          apiParams={{ IsActive: true }}
                        />
                        {errors.selectedNeighborhoods && touched.selectedNeighborhoods && (
                          <FormHelperText error>{errors.selectedNeighborhoods}</FormHelperText>
                        )}
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <Button type="submit" startIcon={<Save />} variant="contained" color="primary">
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Box>
      </Fade>
    </Modal>
  );
};
